// Minimal tween model, stepped from the single rAF loop. No dependency on a tween
// library — we only need per-object position + opacity lerps with smoothstep and
// interrupt handling.

export function smoothstep(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

export interface Tween {
  /** Monotonic key so a new tween for the same target replaces the old one. */
  key: string;
  startMs: number;
  durationMs: number;
  /** Called each frame with eased 0..1; returns false when it wants to stop early. */
  step: (eased: number) => void;
  /** Called once when the tween reaches 1. */
  done?: () => void;
}

export class TweenRunner {
  private tweens = new Map<string, Tween>();

  /** Add or replace the tween for a key (replacement = interrupt from current). */
  set(t: Tween): void {
    this.tweens.set(t.key, t);
  }

  cancel(key: string): void {
    this.tweens.delete(key);
  }

  has(key: string): boolean {
    return this.tweens.has(key);
  }

  get size(): number {
    return this.tweens.size;
  }

  /** Advance all tweens. `nowMs` from performance.now(). */
  update(nowMs: number): void {
    for (const [key, t] of this.tweens) {
      const raw = (nowMs - t.startMs) / t.durationMs;
      const eased = smoothstep(raw);
      t.step(eased);
      if (raw >= 1) {
        this.tweens.delete(key);
        t.done?.();
      }
    }
  }

  clear(): void {
    this.tweens.clear();
  }
}
